import { ArrowRight, ArrowLeft, Phone, X, Camera } from "lucide-react";
import { caseStudies, withBase, type CaseStudy } from "@/data/case-studies";

const ember = "hsl(18 82% 52%)";
const base = () => import.meta.env?.BASE_URL || "/";

function StudyCard({ study, basePath }: { study: CaseStudy; basePath: string }) {
  return (
    <a href={withBase(study.path, basePath)} data-testid={`card-case-study-${study.slug}`}
      className="group grid sm:grid-cols-5 bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
      <div className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto overflow-hidden bg-secondary">
        <img src={study.image} alt={study.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        {study.stageNote && <span className="absolute left-3 top-3 rounded-full bg-slate-950/80 text-white text-[0.7rem] font-semibold uppercase tracking-wider px-2.5 py-1">Installation stage</span>}
      </div>
      <div className="sm:col-span-3 p-6 flex flex-col">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">{study.client} · {study.location}</p>
        <h3 className="font-display font-bold text-2xl leading-tight mb-2">{study.shortTitle}</h3>
        <p className="text-muted-foreground leading-relaxed text-sm mb-4">{study.summary}</p>
        <span className="mt-auto inline-flex items-center gap-2 font-semibold text-primary">Read case study <ArrowRight aria-hidden className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </a>
  );
}

export function CaseStudiesContent({ basePath = base() }: { basePath?: string }) {
  return (
    <>
      <section className="bg-slate-950 text-white">
        <div className="container mx-auto px-4 max-w-7xl py-12 lg:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-300 mb-4">Project case studies</p>
          <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.03] max-w-3xl mb-4" data-testid="text-case-studies-title">Work we have done for gyms, photographed on site.</h1>
          <p className="text-lg text-white/70 max-w-2xl">Our own photographs from real installations. We only state what we can confirm, and we label work-in-progress images as installation stage.</p>
        </div>
      </section>
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 max-w-7xl grid gap-6">
          {caseStudies.map(s => <StudyCard key={s.slug} study={s} basePath={basePath} />)}
        </div>
      </section>
      <Cta basePath={basePath} quoteHref="/quote" label="Planning work at your gym?" />
    </>
  );
}

function Cta({ basePath, quoteHref, label }: { basePath: string; quoteHref: string; label: string }) {
  return (
    <section className="bg-foreground text-background">
      <div className="container mx-auto px-4 max-w-7xl py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-1">{label}</h2>
          <p className="text-background/70">Tell us about the space and we will arrange a site survey.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={withBase(quoteHref, basePath)} data-testid="link-case-study-quote" className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-7 font-bold bg-primary text-primary-foreground hover:opacity-90">Request a quote <ArrowRight aria-hidden className="w-4 h-4" /></a>
          <a href="tel:02080870906" data-testid="link-case-study-phone" className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-7 font-semibold border border-background/25 hover:bg-background/10"><Phone aria-hidden className="w-4 h-4" /> 020 8087 0906</a>
        </div>
      </div>
    </section>
  );
}

export function CaseStudyContent({ study, basePath = base() }: { study: CaseStudy; basePath?: string }) {
  const others = caseStudies.filter(s => s.slug !== study.slug);
  const isSauna = study.serviceHref.startsWith("/sauna");
  return (
    <>
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <img src={study.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        <div className="container relative mx-auto px-4 max-w-7xl py-10 lg:py-14">
          <a href={withBase("/case-studies", basePath)} data-testid="link-back-case-studies" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white mb-6"><ArrowLeft aria-hidden className="w-4 h-4" /> All case studies</a>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: isSauna ? "hsl(22 90% 66%)" : "rgb(125 211 252)" }}>{study.service} · {study.client} {study.location}</p>
          <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.03] max-w-3xl mb-4" data-testid="text-case-study-title">{study.client} {study.location}: {study.shortTitle.charAt(0).toLowerCase() + study.shortTitle.slice(1)}</h1>
          <p className="text-lg text-white/75 max-w-2xl">{study.summary}</p>
        </div>
      </section>

      <section className="py-10 lg:py-12 border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-12 gap-8">
          <dl className="lg:col-span-5 grid grid-cols-2 gap-px bg-border border border-border rounded-2xl overflow-hidden self-start">
            {study.facts.map(f => (
              <div key={f.label} className="bg-card p-4">
                <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{f.label}</dt>
                <dd className="font-display font-bold text-lg leading-snug" data-testid={`text-fact-${f.label.toLowerCase()}`}>{f.value}</dd>
              </div>
            ))}
          </dl>
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-2xl mb-3">What the project involved</h2>
            <ul className="space-y-2 mb-5">
              {study.scope.map(s => <li key={s} className="flex gap-3 text-muted-foreground"><span aria-hidden className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: isSauna ? ember : "hsl(var(--primary))" }} />{s}</li>)}
            </ul>
            {study.stageNote && <p className="flex gap-2 items-start text-sm rounded-xl border px-4 py-3 bg-secondary/60" data-testid="text-stage-note"><Camera aria-hidden className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ember }} />{study.stageNote}</p>}
            <div className="flex flex-wrap gap-4 mt-5 text-sm font-semibold">
              <a href={withBase(study.serviceHref, basePath)} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="link-case-study-service">{study.serviceLabel} service <ArrowRight aria-hidden className="w-4 h-4" /></a>
              <a href={withBase("/contact", basePath)} className="inline-flex items-center gap-1.5 hover:underline" data-testid="link-case-study-contact">Talk to an engineer</a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-12" aria-labelledby="gallery-title">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-end justify-between gap-4 mb-5">
            <h2 id="gallery-title" className="font-display font-bold text-2xl md:text-3xl">Project photographs</h2>
            <p className="text-sm text-muted-foreground">{study.photos.length} photos · select to enlarge</p>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {study.photos.map((p, i) => (
              <li key={p.src} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <figure className="h-full flex flex-col">
                  <a href={`#photo-${i + 1}`} data-testid={`button-enlarge-photo-${i + 1}`} aria-label={`Enlarge photo ${i + 1}: ${p.caption}`} className="group relative block flex-1 min-h-0 overflow-hidden rounded-xl bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                    <img src={p.src} alt={p.alt} loading={i < 4 ? "eager" : "lazy"} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${i === 0 ? "aspect-square" : "aspect-[4/5]"}`} />
                    {p.stage && <span className="absolute left-2 top-2 rounded-full bg-slate-950/80 text-white text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5">{p.stage}</span>}
                  </a>
                  <figcaption className="text-xs text-muted-foreground mt-1.5 leading-snug">{p.caption}</figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </div>
        {study.photos.map((p, i) => (
          <div key={p.src} id={`photo-${i + 1}`} role="dialog" aria-label={p.caption} className="hidden target:flex fixed inset-0 z-[100] bg-slate-950/95 items-center justify-center p-4">
            <a href="#gallery-title" aria-label="Close enlarged photo" data-testid={`button-close-photo-${i + 1}`} className="absolute inset-0" />
            <figure className="relative max-w-5xl w-full flex flex-col items-center pointer-events-none">
              <img src={p.src} alt={p.alt} loading="lazy" className="max-h-[80dvh] w-auto rounded-lg" />
              <figcaption className="text-white/80 text-sm mt-3 text-center">{p.stage ? `${p.stage}: ` : ""}{p.caption} <span className="text-white/50">({i + 1} / {study.photos.length})</span></figcaption>
            </figure>
            <a href="#gallery-title" aria-label="Close" className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20"><X aria-hidden className="w-5 h-5" /></a>
            {i > 0 && <a href={`#photo-${i}`} aria-label="Previous photo" className="absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20"><ArrowLeft aria-hidden className="w-5 h-5" /></a>}
            {i < study.photos.length - 1 && <a href={`#photo-${i + 2}`} aria-label="Next photo" className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20"><ArrowRight aria-hidden className="w-5 h-5" /></a>}
          </div>
        ))}
      </section>

      {others.length > 0 && (
        <section className="pb-10 lg:pb-14">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="font-display font-bold text-xl mb-4">More projects</h2>
            <div className="grid gap-4">
              {others.map(s => <StudyCard key={s.slug} study={s} basePath={basePath} />)}
            </div>
          </div>
        </section>
      )}
      <Cta basePath={basePath} quoteHref={study.quoteHref} label={isSauna ? "Planning a sauna for your gym?" : "Need cooling for your gym floor?"} />
    </>
  );
}
